import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

/**
 * HeroBanner (Auth pages — Login / Signup / Forgot)
 *
 * Premium auto-rotating promo banner slider. Banners are managed from the admin
 * panel (Marketing → Login Banners) and fetched from /api/public/auth-banners;
 * if the API is empty/unreachable it falls back to the bundled defaults. Each
 * slide fills the card with a softly blurred extension of its own artwork (so
 * nothing is ever cropped), with the full banner shown crisply on top. Fully
 * responsive + touch-swipe, fade transitions, dots, prev/next and auto-play.
 */
const FALLBACK = [
  { image: "/banners/banner1.jpg", title: "IMO, WhatsApp & Telegram bonus offer" },
  { image: "/banners/banner2.jpg", title: "Bonus gift on every step" },
  { image: "/banners/banner3.jpg", title: "Unforgettable gaming experience" },
  { image: "/banners/banner4.jpg", title: "100% welcome bonus on all games" },
];

const INTERVAL = 4500;
const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

export const HeroBanner = () => {
  const { pathname } = useLocation();
  const page = (pathname || "").toLowerCase().includes("register") ? "register" : "login";
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const paused = useRef(false);
  const touchX = useRef(null);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/public/auth-banners`, { params: { page } })
      .then(({ data }) => {
        // Respect the admin's list exactly — an empty array means the admin
        // removed all banners, so show none. Only fall back to bundled defaults
        // when the API itself is unreachable (network/server error).
        if (alive && Array.isArray(data)) setBanners(data);
      })
      .catch(() => { if (alive) setBanners(FALLBACK); });
    return () => { alive = false; };
  }, [page]);

  const count = banners.length;
  const go = useCallback(
    (n) => setIdx(((n % count) + count) % count),
    [count]
  );

  useEffect(() => {
    if (idx >= count) setIdx(0);
  }, [count, idx]);

  useEffect(() => {
    if (count <= 1) return undefined;
    const t = setInterval(() => {
      if (!paused.current) setIdx((i) => (i + 1) % count);
    }, INTERVAL);
    return () => clearInterval(t);
  }, [count]);

  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
    paused.current = true;
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? idx + 1 : idx - 1);
    touchX.current = null;
    paused.current = false;
  };

  const Slide = ({ b, active, i }) => {
    const inner = (
      <>
        <img
          src={b.image}
          alt=""
          aria-hidden
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover scale-125 blur-2xl opacity-60"
        />
        <div className="absolute inset-0 bg-black/25" />
        {/* Mobile-specific artwork if provided, else desktop image on all sizes */}
        {b.image_mobile ? (
          <>
            <img
              src={b.image_mobile}
              alt={b.title || ""}
              draggable={false}
              className="relative h-full w-full object-contain select-none sm:hidden"
              style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.45))" }}
            />
            <img
              src={b.image}
              alt={b.title || ""}
              draggable={false}
              className="relative h-full w-full object-contain select-none hidden sm:block"
              style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.45))" }}
            />
          </>
        ) : (
          <img
            src={b.image}
            alt={b.title || ""}
            draggable={false}
            className="relative h-full w-full object-contain select-none"
            style={{ filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.45))" }}
          />
        )}
      </>
    );
    const cls = "absolute inset-0 transition-opacity duration-700 ease-out";
    const style = { opacity: active ? 1 : 0, zIndex: active ? 2 : 1 };
    return b.link ? (
      <a
        key={b.image + i}
        href={b.link}
        target="_blank"
        rel="noreferrer"
        className={cls}
        style={style}
        aria-hidden={!active}
        data-testid={`auth-banner-slide-${i}`}
      >
        {inner}
      </a>
    ) : (
      <div
        key={b.image + i}
        className={cls}
        style={style}
        aria-hidden={!active}
        data-testid={`auth-banner-slide-${i}`}
      >
        {inner}
      </div>
    );
  };

  // No banners configured (admin removed all) → render nothing on auth pages.
  if (count === 0) return null;

  return (
    <div data-testid="auth-hero-banner" className="auth-hero w-full px-3 sm:px-6 pt-3 sm:pt-5">
      <div
        className="relative mx-auto w-full max-w-[1120px] overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-[0_24px_70px_-24px_rgba(0,0,0,0.85)] bg-[#07100a]"
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="absolute inset-x-0 top-0 h-[3px] z-30 bg-gradient-to-r from-transparent via-[#21c46e] to-transparent" />

        <div className="relative h-[132px] xs:h-[150px] sm:h-[205px] md:h-[260px] lg:h-[300px]">
          {banners.map((b, i) => (
            <Slide key={b.id || b.image + i} b={b} active={i === idx} i={i} />
          ))}

          <div className="pointer-events-none absolute inset-y-0 left-0 w-10 sm:w-16 bg-gradient-to-r from-black/40 to-transparent z-10" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-10 sm:w-16 bg-gradient-to-l from-black/40 to-transparent z-10" />
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous banner"
              data-testid="auth-banner-prev"
              onClick={() => go(idx - 1)}
              className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 z-20 h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-full bg-black/40 hover:bg-[#19a55c] text-white backdrop-blur ring-1 ring-white/15 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            </button>
            <button
              type="button"
              aria-label="Next banner"
              data-testid="auth-banner-next"
              onClick={() => go(idx + 1)}
              className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 z-20 h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-full bg-black/40 hover:bg-[#19a55c] text-white backdrop-blur ring-1 ring-white/15 transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>

            <div className="absolute inset-x-0 bottom-2 sm:bottom-3 z-20 flex items-center justify-center gap-1.5 sm:gap-2">
              {banners.map((b, i) => (
                <button
                  key={b.id || b.image + i}
                  type="button"
                  aria-label={`Go to banner ${i + 1}`}
                  data-testid={`auth-banner-dot-${i}`}
                  onClick={() => go(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? "w-6 bg-[#21c46e] shadow-[0_0_10px_rgba(33,196,110,0.7)]" : "w-1.5 bg-white/45 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
